import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    EventEmitter,
    inject,
    InjectionToken,
    Input,
    input,
    OnDestroy,
    OnInit,
    Output,
    Self,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NgControl,
    ReactiveFormsModule,
    ValidationErrors,
    Validator,
    Validators,
} from '@angular/forms';
import {v4 as uuidv4} from 'uuid';
import {DateTime} from 'luxon';
import {CommonModule} from '@angular/common';
import {
    NgbDateAdapter,
    NgbDateParserFormatter,
    NgbDateStruct,
    NgbDateStructAdapter,
    NgbModule,
} from '@ng-bootstrap/ng-bootstrap';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faCalendarAlt} from '@fortawesome/free-solid-svg-icons';
import {ControlMessagesComponent} from '../control-messages/control-messages.component';
import {DateParserFormatter} from './date-parser-formatter';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {distinctUntilChanged} from 'rxjs/operators';
import {PopoverIconComponent} from '../popover-icon/popover-icon.component';
import {ICON_STYLE_DEFAULTS} from '../icon-style';
import {patchControlMethod} from '../patch-control-method';

export const INPUT_DATE_GROUP_DEFAULTS = new InjectionToken<{
    outsideDays?: 'visible' | 'hidden' | 'collapsed';
}>('INPUT_DATE_GROUP_DEFAULTS');

@Component({
    selector: 'app-input-date-group',
    styleUrls: ['./input-date-group.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        NgbModule,
        FontAwesomeModule,
        ControlMessagesComponent,
        PopoverIconComponent,
    ],
    providers: [
        {
            provide: NgbDateParserFormatter,
            useClass: DateParserFormatter,
        },
        // Own the ngb date-model contract (NgbDateStruct). Without this, an app that provides a custom
        // NgbDateAdapter (ngwebbill's DateTime adapter) leaks in and rejects the struct this component
        // feeds the input, leaving the field blank. AGI-19199.
        {
            provide: NgbDateAdapter,
            useClass: NgbDateStructAdapter,
        },
    ],
    templateUrl: './input-date-group.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputDateGroupComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
    @Input() label = '';
    /**
     * Explicit required flag. When provided it wins; when left undefined the asterisk is derived
     * from a `Validators.required` on the control (matching InputTextGroupComponent).
     */
    @Input() required: boolean | undefined;
    @Input() name: string | undefined;
    @Input() information: string | undefined;
    /**
     * When set, renders the Bootstrap grid layout (label in a `col-{labelCols}`, field in the
     * rest) instead of the default stacked layout. ngwebbill passes a value (e.g. 4) to match its
     * legacy app-local component; other apps leave it unset for the stacked layout.
     */
    @Input() labelCols: number | null = null;
    /** Optional visual state styling on the input (ngwebbill: saved / error highlight). */
    @Input() state: 'updated' | 'error' | undefined;
    /** Force the invalid styling regardless of control state (ngwebbill). */
    @Input() forceInvalid = false;
    /** Select the input text when the field gains focus (ngwebbill). */
    @Input() selectOnFocus = false;
    /** Optional read-only username shown next to the field (ngwebbill). */
    @Input() userName: string | undefined;
    /** `autocomplete` attribute on the input. Default `on`. */
    @Input() autoComplete: 'on' | 'off' = 'on';
    readonly faCalendarAlt = faCalendarAlt;

    /** Set in ngOnInit from the control's required validator; used when `required` is not set explicitly. */
    private requiredFromValidator = false;
    /** When true, suppress the default `mb-3` bottom margin even if a label is shown. */
    readonly noBottomMargin = input(false);

    private readonly defaults = inject(INPUT_DATE_GROUP_DEFAULTS, {optional: true});
    private readonly iconStyleDefaults = inject(ICON_STYLE_DEFAULTS, {optional: true});

    readonly outsideDays = input<'visible' | 'hidden' | 'collapsed' | undefined>();
    readonly outsideDaysComputed = computed(
        () => this.outsideDays() ?? this.defaults?.outsideDays ?? 'visible',
    );
    readonly iconStyleComputed = computed(() =>
        this.iconStyleDefaults?.iconStyle === 'fontawesome' ? 'fontawesome' : 'bi',
    );

    readonly restoreFocusElement = document.body;

    private readonly defaultMinDate: NgbDateStruct = {
        year: DateTime.now().minus({years: 1000}).year,
        month: 1,
        day: 1,
    };
    private readonly defaultMaxDate: NgbDateStruct = {
        year: DateTime.now().plus({years: 1000}).year,
        month: 1,
        day: 1,
    };

    @Input() minDate: NgbDateStruct | undefined | null;
    @Input() maxDate: NgbDateStruct | undefined | null;
    @Output() focusEvent = new EventEmitter<string>();

    // ngbDatepicker uses an NgbDateStruct when a date is selected, a string if the input is invalid, and null if no
    // date is entered.
    formControl = new FormControl<NgbDateStruct | string | null>(null);
    id: string = uuidv4();
    onTouch: (() => void) | undefined;

    private onChange: ((value: DateTime | null) => void) | undefined;

    private restoreMarkAsFunctions: (() => void) | undefined;

    constructor(
        @Self() public ngControl: NgControl,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        ngControl.valueAccessor = this;
        this.formControl.valueChanges
            .pipe(
                takeUntilDestroyed(),
                distinctUntilChanged(
                    (prev: NgbDateStruct | string | null, curr: NgbDateStruct | string | null) => {
                        if (prev === curr) {
                            return true;
                        }
                        if (prev === null || curr === null) {
                            return prev === curr;
                        }
                        if (typeof prev === 'object' && typeof curr === 'object') {
                            return (
                                prev.year === curr.year &&
                                prev.month === curr.month &&
                                prev.day === curr.day
                            );
                        }
                        return false;
                    },
                ),
            )
            .subscribe(newValue => {
                if (newValue == null) {
                    this.onChange?.(null);
                    return;
                }

                if (typeof newValue === 'object') {
                    const newDateTime = DateTime.fromObject({
                        year: newValue.year,
                        month: newValue.month,
                        day: newValue.day,
                    });
                    this.onChange?.(newDateTime);
                    return;
                }

                const invalidDateTime = DateTime.invalid(
                    'Parse error',
                    `Can't parse "${newValue}" as a date`,
                );
                this.onChange?.(invalidDateTime);
            });
    }

    get minDateValue(): NgbDateStruct {
        return this.minDate ?? this.defaultMinDate;
    }

    get maxDateValue(): NgbDateStruct {
        return this.maxDate ?? this.defaultMaxDate;
    }

    get labelClass(): string {
        const required = (this.required ?? this.requiredFromValidator) ? 'required' : '';
        // Grid layout uses Bootstrap column classes; the stacked layout uses `form-label`.
        return this.labelCols != null
            ? `col-${this.labelCols} ${required}`.trim()
            : `form-label ${required}`.trim();
    }

    get inputColsClass(): string {
        return this.labelCols != null ? `col-${12 - this.labelCols}` : '';
    }

    focus(): void {
        this.focusEvent.emit(this.name);
    }

    // Chromium fires a plain keydown Event, with no key, when an autofill suggestion is picked.
    keyPress($event: Event): void {
        if ($event instanceof KeyboardEvent && $event.key.toLowerCase() === 't') {
            // Enter today at midnight
            this.formControl.setValue(
                DateTime.local(DateTime.local().year, DateTime.local().month, DateTime.local().day),
            );
            $event.preventDefault();
        }
    }

    ngOnInit(): void {
        this.ngControl.control?.addValidators([this.boundValidate]);

        // The field renders an invalid DateTime as blank, so don't leave one in the form. (AGI-19494)
        const writtenValue: unknown = this.ngControl.control?.value;
        if (writtenValue instanceof DateTime && !writtenValue.isValid) {
            this.ngControl.control?.setValue(null, {emitEvent: false});
        }

        // The required asterisk is derived from the control's `Validators.required`, matching
        // InputTextGroupComponent; an explicit `required` input still wins (see labelClass).
        this.requiredFromValidator =
            this.ngControl.control?.hasValidator(Validators.required) ?? false;

        // Then using ChangeDetectionStrategy.OnPush the component doesn't notice, when the control is marked as
        // (un)touched, and will therefore not show validation errors. However, Angular doesn't offer a simple way of
        // listening to, when a control is marked as (un)touched, that we can hook into. So we have to reassign the
        // markAs(un)Touched() method in order to call detectChanges().
        const control = this.ngControl.control;
        if (control != null) {
            const detectChanges = (): void => this.changeDetectorRef.detectChanges();
            const restoreTouched = patchControlMethod(control, 'markAsTouched', detectChanges);
            const restoreUntouched = patchControlMethod(control, 'markAsUntouched', detectChanges);
            this.restoreMarkAsFunctions = (): void => {
                restoreTouched();
                restoreUntouched();
            };
        }
    }

    /** Angular's `cleanUpControl` does not remove the validator added above. (AGI-19494) */
    ngOnDestroy(): void {
        this.restoreMarkAsFunctions?.();

        const control = this.ngControl.control;
        if (control == null) {
            return;
        }
        control.removeValidators(this.boundValidate);
        // removeValidators only unregisters; the stale errors stay until validation re-runs.
        control.updateValueAndValidity({emitEvent: false});
    }

    registerOnChange(fn: (value: DateTime | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    /**
     * After a calendar pick ngbDatepicker restores focus to `restoreFocusElement` (document.body),
     * so the input never fires the (blur) that commits a control configured with `updateOn: 'blur'`.
     * Its pending value would then stay uncommitted until the user re-focused and blurred the field,
     * leaving dependent logic (e.g. an enable/disable that reacts to the date) stale and risking a
     * save without the picked date. Committing the touched state on selection flushes that pending
     * value immediately. Harmless for the default `updateOn: 'change'` case (value already committed).
     * (AGI-19156)
     */
    protected onDateSelect(): void {
        this.onTouch?.();
    }

    writeValue(value: DateTime | null): void {
        // An invalid DateTime answers NaN for year/month/day, and luxon throws on a NaN date.
        const ngbValue =
            value == null || !value.isValid
                ? null
                : {year: value.year, month: value.month, day: value.day};
        this.formControl.setValue(ngbValue, {emitEvent: false});
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.formControl.disable();
        } else {
            this.formControl.enable();
        }
    }

    validate(_: AbstractControl): ValidationErrors | null {
        return this.formControl.errors;
    }

    /** One stable reference: `removeValidators` matches by identity. */
    private readonly boundValidate = (control: AbstractControl): ValidationErrors | null =>
        this.validate(control);
}
